import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownItemAnchor,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "@/components/catalyst/navbar";
import {
  Sidebar as BaseSidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarItemButton,
  SidebarItemLink,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "@/components/catalyst/sidebar";
import { SidebarLayout } from "@/components/catalyst/sidebar-layout";
import { Badge } from "@/components/catalyst/badge";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/16/solid";
import {
  ArrowsRightLeftIcon,
  ArrowPathRoundedSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  HandThumbUpIcon,
  PhoneArrowUpRightIcon,
  HomeIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/20/solid";
import { useAuth } from "@/lib/Auth/useAuth";
import { useOptionalUser } from "@/lib/Auth/useUser";
import { SidebarHeaderDropdown } from "@/lib/Components/Layout/Sidebar/SidebarHeaderDropdown";
import {
  FeatureFlagType,
  RelocationStatus,
  WorkOrderStatus,
} from "@/gql/graphql";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { ReactNode, useState } from "react";
import {
  useMatchRoute,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageSelectorInline } from "@/lib/i18n/LanguageSelector";
import { useTranslations } from "use-intl";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { metaQuery } from "@/app/Meta/GraphQL/metaQuery";
import { Snowfall, useIsChristmas } from "@/lib/Components/Layout/Christmas";
import { WorkOrdersNotificationIndicator } from "@/app/ProDrivers/WorkOrders/Components/WorkOrdersNotificationIndicator";
import { TenantViewToggle } from "@/app/Organisations/Components/TenantViewToggle";
import { CallbackLeadsNotificationIndicator } from "@/app/CallbackLeads/Components/CallbackLeadsNotificationIndicator";
import {
  AdminNavigation,
  useIsAdminRoute,
} from "@/app/Admin/Components/AdminNavigation";

export function AppShell({ children }: { children: ReactNode }) {
  "use no memo";

  const isChristmas = useIsChristmas();
  const { signOut } = useAuth();
  const user = useOptionalUser();
  const { supplierId: supplierParam } = useParams({ strict: false }) as {
    supplierId?: string;
    tenant?: string;
    orgId?: string;
  };
  const supplierId = supplierParamToId(supplierParam);
  const hasSupplier = !!supplierId;
  const { isAdmin } = useTenant();
  const { data: metaData } = useGqlQuery(metaQuery, {});
  const is_verified = metaData?.meta.is_verified ?? false;
  const gapRentalsEnabled = !!metaData?.meta.feature_flags.find(
    (flag) => flag.type === FeatureFlagType.GapRentals,
  )?.enabled;
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  // `{-$supplierId}` stays optional in the match pattern, so with no supplier
  // selected `/supplier/<anything>` matches the dashboard. Compare what the
  // match swallowed against the real param to reject those.
  const homeMatch = matchRoute({
    to: "/$tenant/$orgId/supplier/{-$supplierId}",
  });
  const isDashboard = homeMatch && homeMatch.supplierId === supplierParam;
  const t = useTranslations("navigation");
  const pathname = useRouterState({
    select: (state) =>
      (state.isLoading
        ? (state.resolvedLocation ?? state.location)
        : state.location
      ).pathname,
  });
  const isAdminRoute = useIsAdminRoute();
  const [adminToggle, setAdminToggle] = useState<{
    pathname: string;
    open: boolean;
  } | null>(null);
  const isAdminOpen =
    adminToggle?.pathname === pathname ? adminToggle.open : isAdminRoute;

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar
                  src={user?.profile_picture_url ?? undefined}
                  initials={user?.name ? getInitials(user.name) : undefined}
                  square
                />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                <DropdownItem
                  onClick={() =>
                    navigate({
                      from: "/$tenant/$orgId/supplier/{-$supplierId}",
                      to: "/$tenant/$orgId/supplier/{-$supplierId}/profile",
                      params: (prev) => prev,
                    })
                  }
                >
                  <UserIcon />
                  <DropdownLabel>{t("myProfile")}</DropdownLabel>
                </DropdownItem>
                <DropdownDivider />
                <DropdownItemAnchor href="https://www.imoova.com/privacy-policy">
                  <ShieldCheckIcon />
                  <DropdownLabel>{t("privacyPolicy")}</DropdownLabel>
                </DropdownItemAnchor>
                <DropdownItemAnchor href="mailto:tradepartners@imoova.com?subject=Website Feedback">
                  <LightBulbIcon />
                  <DropdownLabel>{t("shareFeedback")}</DropdownLabel>
                </DropdownItemAnchor>
                <DropdownDivider />
                <LanguageSelectorInline />
                <DropdownDivider />
                <DropdownItem
                  onClick={() => {
                    return signOut({ redirectUrl: "/sign-in" });
                  }}
                >
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>{t("signOut")}</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <BaseSidebar>
          {isChristmas ? (
            <Snowfall
              count={30}
              className="!absolute left-0 top-0 z-10 h-[160px] w-full"
            />
          ) : null}
          <SidebarHeader>
            <SidebarHeaderDropdown />
          </SidebarHeader>
          <SidebarBody className="overflow-x-hidden">
            <AnimatePresence mode="popLayout" initial={false}>
              {isAdmin && isAdminOpen ? (
                <motion.div
                  key="admin"
                  initial={{ x: "100%", opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: "100%", opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="[&>[data-slot=section]+[data-slot=section]]:mt-8"
                >
                  <SidebarSection>
                    <SidebarItemButton
                      onClick={() => setAdminToggle({ pathname, open: false })}
                    >
                      <ChevronLeftIcon />
                      <SidebarLabel>{t("back")}</SidebarLabel>
                    </SidebarItemButton>
                  </SidebarSection>
                  <AdminNavigation />
                </motion.div>
              ) : (
                <motion.div
                  key="main"
                  initial={{ x: "-100%", opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: "-100%", opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <SidebarSection>
                    <SidebarItemLink
                      from="/$tenant/$orgId/supplier/{-$supplierId}"
                      to="/$tenant/$orgId/supplier/{-$supplierId}"
                      params={(prev) => prev}
                      current={!!isDashboard}
                      preload="intent"
                      viewTransition
                    >
                      <HomeIcon />
                      <SidebarLabel>{t("home")}</SidebarLabel>
                    </SidebarItemLink>
                    <SidebarItemLink
                      to="/$tenant/$orgId/supplier/{-$supplierId}/bookings"
                      params={(prev) => prev}
                      current={
                        !!matchRoute({
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings",
                          fuzzy: true,
                        })
                      }
                      search={{
                        bookingStatus: [],
                      }}
                      preload="intent"
                      viewTransition
                    >
                      <TicketIcon />
                      <SidebarLabel>{t("bookings")}</SidebarLabel>
                    </SidebarItemLink>
                    <SidebarItemLink
                      from="/$tenant/$orgId/supplier/{-$supplierId}"
                      to="/$tenant/$orgId/supplier/{-$supplierId}/relocations"
                      params={(prev) => prev}
                      current={
                        !!matchRoute({
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations",
                          fuzzy: true,
                        })
                      }
                      search={{
                        relocationStatus: is_verified
                          ? [RelocationStatus.Ready, RelocationStatus.Paused]
                          : [RelocationStatus.Draft],
                      }}
                      preload="intent"
                      viewTransition
                    >
                      <ArrowsRightLeftIcon />
                      <SidebarLabel>{t("relocations")}</SidebarLabel>
                    </SidebarItemLink>
                    {gapRentalsEnabled ? (
                      <SidebarItemLink
                        from="/$tenant/$orgId/supplier/{-$supplierId}"
                        to="/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals"
                        params={(prev) => prev}
                        current={
                          !!matchRoute({
                            to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals",
                            fuzzy: true,
                          })
                        }
                        search={{
                          relocationStatus: is_verified
                            ? [RelocationStatus.Ready, RelocationStatus.Paused]
                            : [RelocationStatus.Draft],
                        }}
                        preload="intent"
                        viewTransition
                      >
                        <ArrowPathRoundedSquareIcon />
                        <SidebarLabel>{t("gapRentals")}</SidebarLabel>
                        <Badge color="emerald" className="ml-auto">
                          {t("new")}
                        </Badge>
                      </SidebarItemLink>
                    ) : null}

                    <SidebarItemLink
                      from="/$tenant/$orgId/supplier/{-$supplierId}"
                      to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders"
                      params={(prev) => prev}
                      preload="intent"
                      search={{
                        workOrderStatus: [
                          WorkOrderStatus.QuoteRequested,
                          WorkOrderStatus.QuoteIssued,
                          WorkOrderStatus.AssigningDrivers,
                          WorkOrderStatus.DriversAssigned,
                          WorkOrderStatus.InProgress,
                          WorkOrderStatus.Invoiced,
                        ],
                      }}
                      current={
                        !!matchRoute({
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers",
                          fuzzy: true,
                        })
                      }
                      viewTransition
                    >
                      <HandThumbUpIcon />
                      <SidebarLabel>{t("proDrivers")}</SidebarLabel>
                      <WorkOrdersNotificationIndicator />
                    </SidebarItemLink>

                    <SidebarDivider />
                    <SidebarItemLink
                      from="/$tenant/$orgId/supplier/{-$supplierId}"
                      to={
                        hasSupplier
                          ? "/$tenant/$orgId/supplier/{-$supplierId}/settings"
                          : "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers"
                      }
                      params={(prev) => prev}
                      search={(prev) => prev}
                      current={
                        !!matchRoute({
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
                          fuzzy: true,
                        })
                      }
                      preload="intent"
                      viewTransition
                    >
                      <Cog6ToothIcon />
                      <SidebarLabel>{t("settings")}</SidebarLabel>
                    </SidebarItemLink>

                    {isAdmin ? (
                      <>
                        <SidebarDivider />
                        <SidebarItemButton
                          current={isAdminRoute}
                          onClick={() =>
                            setAdminToggle({ pathname, open: true })
                          }
                        >
                          <WrenchScrewdriverIcon />
                          <SidebarLabel>{t("admin")}</SidebarLabel>
                          <ChevronRightIcon className="ml-auto" />
                        </SidebarItemButton>
                        <SidebarItemLink
                          from="/$tenant/$orgId/supplier/{-$supplierId}"
                          to="/$tenant/$orgId/supplier/{-$supplierId}/callbacks"
                          params={(prev) => prev}
                          current={
                            !!matchRoute({
                              to: "/$tenant/$orgId/supplier/{-$supplierId}/callbacks",
                              fuzzy: true,
                            })
                          }
                          preload="intent"
                          viewTransition
                        >
                          <PhoneArrowUpRightIcon />
                          <SidebarLabel>{t("callbacks")}</SidebarLabel>
                          <CallbackLeadsNotificationIndicator />
                        </SidebarItemLink>
                      </>
                    ) : null}

                    <SidebarDivider />
                  </SidebarSection>
                </motion.div>
              )}
            </AnimatePresence>
            {isAdmin && isAdminOpen ? null : (
              <>
                <SidebarSpacer />
                <SidebarSection>
                  <div className="mx-1 rounded-lg border-2 border-dashed border-zinc-600 p-3">
                    <p className="mb-1 text-xs font-medium text-white">
                      {t("needHelp")}
                    </p>
                    <p className="mt-2 text-xs text-zinc-400">
                      {t("contactUs")}
                    </p>
                    <p className="text-xs font-medium text-white hover:underline">
                      <a href="mailto:admin@imoova.com">admin@imoova.com</a>
                    </p>
                    <p className="mt-2 text-xs text-zinc-400">{t("orCall")}</p>
                    <p className="text-xs font-medium text-white hover:underline">
                      <a href="tel:+61721000193">+617 2100 0193</a>
                    </p>
                  </div>
                </SidebarSection>
              </>
            )}
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden">
            <Dropdown>
              <DropdownButton as={SidebarItemButton}>
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar
                    src={user?.profile_picture_url ?? undefined}
                    initials={user?.name ? getInitials(user.name) : undefined}
                    className="size-10"
                    square
                    alt=""
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm/5 font-medium text-white">
                      {user?.name}
                    </span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-400">
                      {user?.email ?? "--"}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="top start">
                <DropdownItem
                  onClick={() =>
                    navigate({
                      from: "/$tenant/$orgId/supplier/{-$supplierId}",
                      to: "/$tenant/$orgId/supplier/{-$supplierId}/profile",
                      params: (prev) => prev,
                    })
                  }
                >
                  <UserIcon />
                  <DropdownLabel>{t("myProfile")}</DropdownLabel>
                </DropdownItem>
                <DropdownDivider />
                <DropdownItemAnchor href="https://www.imoova.com/privacy-policy">
                  <ShieldCheckIcon />
                  <DropdownLabel>{t("privacyPolicy")}</DropdownLabel>
                </DropdownItemAnchor>
                <DropdownItemAnchor href="mailto:tradepartners@imoova.com?subject=Website Feedback">
                  <LightBulbIcon />
                  <DropdownLabel>{t("shareFeedback")}</DropdownLabel>
                </DropdownItemAnchor>
                <DropdownDivider />
                <DropdownItem
                  onClick={() => {
                    return signOut({ redirectUrl: "/sign-in" });
                  }}
                >
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>{t("signOut")}</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <TenantViewToggle />
          </SidebarFooter>
        </BaseSidebar>
      }
    >
      {children}
    </SidebarLayout>
  );
}
